import React from 'react';
import Link from 'components/commercetools-ui/atoms/link';
import Button from 'components/commercetools-ui/atoms/button';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import { GenericSectionContent, MediaCard } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

// Chronos ← Methven `tiles-multiple-cta`: a section heading + description, then a row (desktop) /
// column (mobile) of image cards, each with title + description + up to two CTAs.
function ChronosCard({ card }: { card: MediaCard }) {
  return (
    <div className={`${SECTION_SPACING} relative w-full overflow-hidden xl:min-h-[30rem] 2xl:min-h-[48rem]`}>
      {card?.desktop_image?.url && (
        <div className="relative h-[20rem] md:h-[24rem] 2xl:h-[35rem]">
          <div className="absolute z-10 h-auto w-full bg-black opacity-20" />
          <Image src={card.desktop_image.url} alt={card?.title ?? ''} className="h-full w-full object-cover" loading="lazy" height={500} width={500} />
        </div>
      )}
      {card?.title && (
        <Typography as="h4" className="pt-16 text-22 font-medium leading-8 text-[#333] xl:pt-4 xl:text-18 2xl:pt-16 2xl:text-22">
          {card.title}
        </Typography>
      )}
      {card?.description && (
        <div
          className="pt-8 text-16 font-normal leading-6 text-[#333] xl:pt-4 xl:text-14 2xl:pt-8 2xl:text-16"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }}
        />
      )}
      {card?.ctas && card.ctas.length > 0 && (
        <div className="flex flex-col gap-10 pt-16 md:flex-row xl:pt-4 2xl:pt-16">
          {card.ctas.map((cta, index) => (
            <Link link={cta?.url ?? '#'} key={index} target={cta?.target || '_self'}>
              <Button
                className={
                  index === 0
                    ? 'border-1 z-10 w-full rounded-none border border-[#2B2C39] bg-white px-18 py-10 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit md:px-32 md:py-18'
                    : 'border-1 z-10 w-full rounded-none border border-[#2B2C39] bg-[#2B2C39] px-18 py-10 shadow-sm hover:!bg-[#394A54] md:w-fit md:px-32 md:py-18'
                }
              >
                <Typography as="span" className={`text-16 xl:text-14 2xl:text-16 ${index === 0 ? 'text-[#090909]' : 'text-white'}`}>
                  {cta?.label}
                </Typography>
              </Button>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChronosRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'ChronosRendering');

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`our-product-container px-24 pt-40 pb-40 md:px-28 lg:px-48 lg:pt-60 lg:pb-60 xl:pl-60 2xl:px-80 2xl:pt-80 2xl:pb-80 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {title && (
          <Typography as="h3" className="mb-24 text-left text-32 font-medium leading-[3.25rem] text-[#090909] xl:text-32 2xl:text-48">
            {title}
          </Typography>
        )}
        {description && (
          <div className="pb-30 text-left text-20 font-normal leading-8 text-[#333] md:text-18 xl:w-[75%] xl:pb-20 xl:text-16 2xl:w-[55%] 2xl:text-20" dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />
        )}
        <div className="flex w-full flex-col gap-32 md:flex-row md:gap-24">
          {cards.map((card, index) => (
            <ChronosCard key={index} card={card} />
          ))}
        </div>
      </div>
    </>
  );
}
